import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import { Script } from 'gatsby';
import { Prose } from '@nikolovlazar/chakra-ui-prose';

import { NonCommerceInfoContext } from '@rsc/contexts/NonCommerceInfoContext';
import { PdpSpecificVideoLockup } from '@rsc/ui-components/tailwind/PdpSpecificVideoLockup';
import ProductDetails from '@rsc/ui-components/sanity/blocks/ProductDetails';
import CarouselRowProductsClipped from '@rsc/ui-components/sanity/blocks/CarouselRowProductsClipped';
import BlockBuilderKlaviyoEmbed from '@rsc/ui-components/sanity/blocks/BlockBuilderKlaviyoEmbed';
import { BlockPropType } from './types';
import CollectionGrid from './CollectionGrid';
import Hero from './Hero';
import ImageHeroesSecondary from './ImageHeroesSecondary';
import CollectionProductCategories from './CollectionProductCategories';
import CarouselRowKeyCollections from './CarouselRowKeyCollections';
import MarketingMoment from './MarketingMoment';
import FeaturedBlogExplore from './FeaturedBlogExplore';
import SearchesFAQ from './SearchesFAQ';
import GridProductCallout from './GridProductCallout';
import UGCGrid from './UGCGrid';
import WhatsNew from './WhatsNew';
import Lifestyle from './Lifestyle';
import ProdGridImage from './ProdGridImage';
import CompanyCallout from './CompanyCallout';
import CommunityGrid from './CommunityGrid';
import SocialGrid from './SocialGrid';
import CarouselRowBlogTopics from './CarouselRowBlogTopics';
import TextAndImageCarousel from './TextAndImageCarousel';
import CardRowReviews from './CardRowReviews';
import CardRowSubCategory from './CardRowSubCategory';
import ImageHeroesBold from './ImageHeroesBold';
import TextHeroesDefault from './TextHeroesDefault';
import TextHeroesBlog from './TextHeroesBlog';
import FeaturedContentWideMedia from './FeaturedContentWideMedia';
import FeaturedBlogPost from './FeaturedBlogPost';
import FeaturedBlogAuthors from './FeaturedBlogAuthors';
import CardRowTwoUp from './CardRowTwoUp';
import CardRowFourThin from './CardRowFourThin';
import CardRowThreePosts from './CardRowThreePosts';
import EditorialContentVideo from './EditorialContentVideo';
import EditorialStatGrid from './EditorialStatGrid';
import FormPress from './FormPress';
import GridPress from './GridPress';
import GridBlogPaginated from './GridBlogPaginated';
import GridBlogPostCallout from './GridBlogPostCallout';
import RichTextBlock from './RichTextBlock';
import EditorialStatement from './EditorialStatement';
import EditorialIconStats from './EditorialIconStats';
import FeaturedStatement from './FeaturedStatement';
import CarouselRowProductsFull from './CarouselRowProductsFull';
import AssetCardRowFourUp from './AssetCardRowFourUp';
import GridBlogAuthors from './GridBlogAuthors';
import DoorFlapFinder from './DoorFlapFinder';
import AppDownloads from './AppDownloads';
import Innotek from './Innotek';
import ThinBanner from './ThinBanner';
import SubscriptionGrid from './SubscriptionGrid';
import SubscriptionActivationDialog from './SubscriptionActivationDialog';
import ComparisonChart from './ComparisonChart';

export default function Blocks({
  blocks,
  data,
  location,
  siteSettings,
  pageContext,
  manuals,
  isPDP,
}) {
  const { nonCommercePdpApprovedBlockTypes, isNonCommerce } = useContext(
    NonCommerceInfoContext
  );

  if (!blocks || !blocks.length) return null;

  if (isNonCommerce) {
    // eslint-disable-next-line no-param-reassign
    blocks = blocks.filter(b =>
      nonCommercePdpApprovedBlockTypes.some(t => b._type === t)
    );
  }

  const socialMedia = siteSettings?.socialMedia || {};

  return blocks
    .flatMap(block => block?.reusableBlockRef?.blocks || block)
    .filter(Boolean)
    .map((block, index) => {
      if (!block._type) {
        console.error(
          `Block _type is undefined. Make sure the _type is being retrieved in sanity-pages.jsx. Error found at path: ${location?.pathname}`
        );
        return null;
      }

      switch (block._type) {
        case 'deptCollectionGrid':
          return (
            <CollectionGrid
              block={block}
              key={block._key}
              data={data}
              location={location}
              pageContext={pageContext}
            />
          );
        case 'deptHtmlBlock':
          if (typeof window === 'undefined') return null;
          return (
            <div key={block._key}>
              {block?.html?.includes('tiktok-embed') && (
                <Script
                  key="tiktok-embed"
                  src="https://www.tiktok.com/embed.js"
                  async
                />
              )}
              <Prose
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(block?.html || '', {
                    USE_PROFILES: { html: true },
                  }),
                }}
              />
            </div>
          );
        case 'deptHero':
          return <Hero block={block} key={block._key} />;
        case 'deptImageHeroesSecondary':
          return <ImageHeroesSecondary block={block} key={block._key} />;
        case 'deptImageHeroesBold':
          return <ImageHeroesBold block={block} key={block._key} />;
        case 'deptTextHeroesDefault':
          return <TextHeroesDefault block={block} key={block._key} />;
        case 'deptTextHeroesBlog':
          return <TextHeroesBlog block={block} key={block._key} />;

        case 'deptFeaturedContentWideMedia':
          return <FeaturedContentWideMedia block={block} key={block._key} />;
        case 'deptFeaturedBlogPost':
          return <FeaturedBlogPost block={block} key={block._key} />;
        case 'deptFeaturedBlogAuthors':
          return <FeaturedBlogAuthors block={block} key={block._key} />;
        case 'deptCardRowSubCategory':
          return <CardRowSubCategory block={block} key={block._key} />;
        case 'deptCardRowTwoUp':
          return <CardRowTwoUp block={block} key={block._key} />;
        case 'deptCardRowFourThin':
          return <CardRowFourThin block={block} key={block._key} />;
        case 'deptCardRowThreePosts':
          return <CardRowThreePosts block={block} key={block._key} />;
        case 'deptEditorialContentVideo':
          return <EditorialContentVideo block={block} key={block._key} />;
        case 'deptEditorialStatGrid':
          return <EditorialStatGrid block={block} key={block._key} />;
        case 'deptFormPress':
          return (
            <FormPress
              block={block}
              key={block._key}
              siteSettings={siteSettings}
            />
          );
        case 'deptGridPress':
          return (
            <GridPress block={block} key={block._key} location={location} />
          );
        case 'deptGridBlogPaginated':
          return (
            <GridBlogPaginated
              key={block._key}
              data={data}
              location={location}
              pageContext={pageContext}
              firstBlockOnPage={index === 0}
              siteSettings={siteSettings}
            />
          );
        case 'deptGridBlogPostCallout':
          return (
            <GridBlogPostCallout
              block={block}
              key={block._key}
              siteSettings={siteSettings}
            />
          );

        case 'deptCollectionProductCategories':
          return (
            <CollectionProductCategories
              block={block}
              key={block._key}
              location={location}
            />
          );
        case 'deptCommunityGrid':
          return <CommunityGrid block={block} key={block._key} />;
        case 'deptCompanyCallout':
          return <CompanyCallout block={block} key={block._key} />;
        case 'blockBuilderPetsafeComparisonChart':
          return <ComparisonChart block={block} key={block._key} />;
        case 'deptKeyCollections':
          return <CarouselRowKeyCollections block={block} key={block._key} />;
        case 'deptLifestyle':
          return <Lifestyle block={block} key={block._key} />;
        case 'deptMarketingMoment':
          return <MarketingMoment block={block} key={block._key} />;
        case 'deptProdGridImage':
          return <ProdGridImage block={block} key={block._key} />;
        case 'deptRelatedArticles':
          return <FeaturedBlogExplore block={block} key={block._key} />;
        case 'deptSearchesFAQ':
          return <SearchesFAQ block={block} key={block._key} isPDP={isPDP} />;
        case 'deptSocialGrid':
          return (
            <SocialGrid
              block={block}
              key={block._key}
              socialMedia={socialMedia}
            />
          );
        case 'blockBuilderSubscriptionGrid':
          return <SubscriptionGrid block={block} key={block._key} />;
        case 'blockBuilderSubscriptionActivationDialog':
          return (
            <SubscriptionActivationDialog block={block} key={block._key} />
          );
        case 'deptTabAndCarousel':
          return <CarouselRowBlogTopics block={block} key={block._key} />;
        case 'deptTextAndImageCarousel':
          return <TextAndImageCarousel block={block} key={block._key} />;
        case 'deptTopSellersGrid':
          return <GridProductCallout block={block} key={block._key} />;
        case 'deptUGCGrid':
          return (
            <UGCGrid block={block} key={block._key} socialMedia={socialMedia} />
          );
        case 'deptWhatsNew':
          return <WhatsNew block={block} key={block._key} />;
        case 'deptCardRowReviews':
          return <CardRowReviews block={block} key={block._key} />;
        case 'deptRichTextBlock':
          // This is sometimes used outside of the Blocks so we add a container here rather than inside the component
          return (
            <div
              className="container mx-auto max-w-[1680px] py-8 px-6 md:px-10 lg:px-20 xl:px-20 2xl:px-20"
              key={block._key}
            >
              <RichTextBlock block={block} />
            </div>
          );
        case 'deptPdpProductDetails':
          return (
            <ProductDetails
              block={block}
              key={block._key}
              siteSettings={siteSettings}
              manuals={manuals}
              data={data}
            />
          );
        case 'deptPdpSpecificVideoLockup':
          return <PdpSpecificVideoLockup block={block} key={block._key} />;
        case 'deptCarouselRowProductsClipped':
          return <CarouselRowProductsClipped block={block} key={block._key} />;
        case 'carouselRowProductsClipped':
          return <CarouselRowProductsClipped block={block} key={block._key} />;
        case 'blockBuilderKlaviyoEmbed':
          return <BlockBuilderKlaviyoEmbed block={block} key={block._key} />;
        case 'deptEditorialStatement':
          return <EditorialStatement block={block} key={block._key} />;
        case 'deptEditorialIconStats':
          return <EditorialIconStats block={block} key={block._key} />;
        case 'deptFeaturedStatement':
          return <FeaturedStatement block={block} key={block._key} />;
        case 'deptCarouselRowProductsFull':
          return <CarouselRowProductsFull block={block} key={block._key} />;
        case 'deptAssetCardRowFourUp':
          return <AssetCardRowFourUp block={block} key={block._key} />;
        case 'deptGridBlogAuthors':
          return (
            <GridBlogAuthors
              block={block}
              location={location}
              key={block._key}
            />
          );
        case 'deptDoorFlapFinder':
          return <DoorFlapFinder key={block._key} block={block} />;
        case 'deptAppDownloads':
          return <AppDownloads block={block} key={block._key} />;
        case 'deptInnotek':
          return <Innotek block={block} key={block._key} />;
        case 'blockBuilderThinBanner':
          return <ThinBanner block={block} key={block._key} />;

        default:
          console.error('=======================================');
          console.error('+++++++++++++++++++++++++++++++++++++++');
          console.error(
            `Block type of ${block._type} is not handled by the Blocks component. Please add a case for that block or explicitly ignore it`
          );
          console.error('+++++++++++++++++++++++++++++++++++++++');
          console.error('=======================================');
          return null;
      }
    });
}

Blocks.defaultProps = {
  pageContext: {},
  manuals: [],
  isPDP: false,
  data: null,
  location: null,
};

Blocks.propTypes = {
  blocks: PropTypes.arrayOf(BlockPropType).isRequired,
  data: PropTypes.shape({
    allAuthors: PropTypes.shape({
      nodes: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string.isRequired,
          language: PropTypes.string,
          image: PropTypes.shape({
            asset: PropTypes.shape({
              url: PropTypes.string,
            }),
            alt: PropTypes.string,
          }),
          title: PropTypes.string,
          occupation: PropTypes.string,
          slug: PropTypes.shape({
            current: PropTypes.string,
          }),
        })
      ),
    }),
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  siteSettings: PropTypes.shape({
    socialMedia: PropTypes.shape({
      facebookBool: PropTypes.bool,
      facebookUrl: PropTypes.string,
      instagramBool: PropTypes.bool,
      instagramUrl: PropTypes.string,
      youtubeBool: PropTypes.bool,
      youtubeUrl: PropTypes.string,
      twitterBool: PropTypes.bool,
      twitterUrl: PropTypes.string,
      pinterestBool: PropTypes.bool,
      pinterestUrl: PropTypes.string,
      tiktokBool: PropTypes.bool,
      tiktokUrl: PropTypes.string,
    }).isRequired,
  }).isRequired,
  pageContext: PropTypes.shape({}),
  manuals: PropTypes.arrayOf(PropTypes.shape({})),
  isPDP: PropTypes.bool,
};
